import { useContext, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { Sample, SampleSearchForm } from 'entities/sample'
import { useEffectSkipFirst, useForm, useQuery } from 'utils/hooks'
import { Alert } from 'react-bootstrap'
import { GlobalStateContext } from 'contexts/global_state_context'
import { Flex } from 'components/shared/flex'
import { A4 } from 'components/shared/print'
import './preview.scss'
import { usePatchSendHistoryApi } from 'api/send_history'
import moment from 'moment'
import styled from 'styled-components'
import { setPageSize } from 'utils/util'
import { SendHistoryForm } from 'entities/send_history'
import _ from 'lodash'
import { Institution } from 'entities/institution'
import { Address } from 'entities/address'
import { useFetchSamplesApi } from 'api/sample'

type Props = {
    history: H.History
}

const Th = styled.th`
    border-right: solid 1px black;
    background-color: #e1e1e1;
    padding: 5px 10px;
    text-align: top;
    font-size: 14px;
    vertical-align: top;
`

const Td = styled.td`
    border-right: solid 1px black;
    padding: 5px;
`
const PreviewRequestListPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const query = useQuery<{ sampleId: string }>()

    const updateForm = useForm<SendHistoryForm>({ status: 'REQUEST_PRINTED' })
    const updateApi = usePatchSendHistoryApi()
    const isHomeTest = !!process.env.REACT_APP_IS_HOME_TEST

    const searchForm = useForm<SampleSearchForm>({
        perPage: 10000,
        ordering: 'sample_created_at',
        sampleIdIn: query.sampleId ? String(query.sampleId) : undefined,
    })
    const samplesApi = useFetchSamplesApi(searchForm)

    useEffect(() => {
        samplesApi.execute()
        setPageSize('portrait')
    }, [])

    useEffectSkipFirst(() => {
        globalState.setLoading(samplesApi.loading)
    }, [samplesApi.loading])

    const getSendUnitInstitutionList = (): Institution[] => {
        // sampleからユニークな病院を抽出するが、1病院あたり5検体を超える場合、5検体区切りで、同一病院の重複を許可する（5検体分がレターパックで送れる最大値のため）
        const uniqInstitutions = _.uniqBy(
            samplesApi.response.results.map((s) => s.institution),
            'id'
        )
        const institutions: Institution[][] = uniqInstitutions.map((institution) => {
            if (!institution) {
                return []
            }
            const chunkCount = Math.ceil((samplesApi.response.results?.filter((s) => s.institution?.id === institution?.id).length ?? 0) / 5)
            return new Array(chunkCount).fill(institution)
        })
        return institutions.flatMap((l) => l)
    }

    const chunk = (): (Address & { name: string; tel?: string })[][] => {
        if (isHomeTest) {
            return _.chunk(
                samplesApi.response.results?.map((sample) => {
                    return { ...sample.patient!.address!, name: `${sample.patient!.familyName} ${sample.patient!.givenName}`, tel: sample.patient!.tel }
                }),
                5
            )
        } else {
            return _.chunk(
                getSendUnitInstitutionList().map((institution) => {
                    return { ...institution.address!, name: institution.name!, tel: institution.tel }
                }),
                5
            )
        }
    }

    const addressView = (address: Address, tel?: string, name?: string) => {
        return (
            <Flex flexDirection="column" style={{ height: '100%' }}>
                <div style={{ height: '50%', fontSize: 14 }}>
                    <div>〒{address.zipCode}</div>
                    <div>
                        {address.prefecture} {address.city} {address.line} {address.building}
                    </div>
                </div>
                <div style={{ height: '30%' }}>{name}</div>
                {tel && <div style={{ height: '20%' }}>TEL:{tel}</div>}
            </Flex>
        )
    }

    const checklistChunk = (): Sample[][] => {
        if (samplesApi.response.results) {
            return _.chunk(
                samplesApi.response.results.sort((a, b) => a.institution!.id! - b.institution!.id!),
                9
            )
        }
        return []
    }

    return (
        <>
            <div className="no-print" style={{ padding: 15 }}>
                <Flex alignItems="flex-end" style={{ marginBottom: 20 }}>
                    <div></div>
                </Flex>
                <div>
                    <Alert>宛先用シール用紙を{chunk().length}枚セットしてください</Alert>
                </div>
            </div>
            {samplesApi.response.results.length > 0 &&
                chunk().map((addressList, pageIndex) => {
                    return (
                        <div key={'page' + pageIndex}>
                            <A4 padding={0} landscape={false} style={{ padding: '21.2mm 18.6mm' }}>
                                <div>
                                    {addressList.map((address, rowIndex) => {
                                        return (
                                            <Flex key={'row-' + rowIndex} style={{ height: '50.8mm' }} className="">
                                                <div style={{ width: '100%', height: '100%', padding: 20 }}>
                                                    {addressView(address, address.tel, `${address.name} ${isHomeTest ? '様' : '御中'}`)}
                                                </div>
                                                <div style={{ width: '100%', height: '100%', padding: 20 }}>
                                                    {addressView(
                                                        {
                                                            zipCode: '464-0814',
                                                            prefecture: '愛知県',
                                                            city: '名古屋市千種区',
                                                            line: '不老町1',
                                                            building: 'NIC711・712号',
                                                        },
                                                        '070-3149-6278',
                                                        'Craif中部検査センター'
                                                    )}
                                                </div>
                                            </Flex>
                                        )
                                    })}
                                </div>
                            </A4>
                        </div>
                    )
                })}

            {checklistChunk().map((samples, index) => {
                return (
                    <A4 padding={10} landscape={false} key={'checklist-' + index}>
                        <div className={'preview-request-list'} style={{ width: '100%', height: '100%', position: 'relative' }}>
                            {/* <div style={{ position: 'absolute', top: -50, left: 0 }}>{api.response.code}</div> */}
                            <div style={{ width: '100%', textAlign: 'center', marginBottom: 30, marginTop: 30 }}>
                                <div style={{ fontSize: 25, fontWeight: 'bold' }}>【二次確認者用】郵便書類チェックリスト</div>
                            </div>
                            <div style={{ marginBottom: 25 }}>
                                <div>① 結果票</div>
                                <div>② 受検いただいた皆様へ</div>
                            </div>
                            <table style={{ width: '100%', border: 'solid 1px black', borderRightWidth: 0, borderBottomWidth: 0, fontSize: 12 }}>
                                <thead>
                                    <tr style={{ borderBottom: 'solid 1px black' }}>
                                        <Th style={{ width: 40 }}>#</Th>
                                        <Th style={{ width: 120 }}>検体コード</Th>
                                        <Th style={{}}>お届け先のお名前</Th>
                                        <Th style={{ width: 140, textAlign: 'center' }}>①</Th>
                                        <Th style={{ width: 50, textAlign: 'center' }}>②</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {new Array(9).fill(0).map((_, sampleIndex) => {
                                        const sample: Sample | undefined = samples[sampleIndex]
                                        return (
                                            <tr key={sample?.id} style={{ borderBottom: 'solid 1px black' }}>
                                                <Td>
                                                    <div style={{ minHeight: 28 }}>{sampleIndex + 1}</div>
                                                </Td>
                                                <Td>{sample?.code}</Td>
                                                <Td style={{ fontSize: 10, padding: '0 5px' }}>
                                                    {isHomeTest
                                                        ? `${sample?.patient?.familyName || ''} ${sample?.patient?.givenName || ''}`
                                                        : sample?.institution?.name}
                                                </Td>
                                                <Td>
                                                    <Flex style={{ fontSize: 10 }} flexWrap="wrap">
                                                        {sample?.productItems?.map((pi, index) => {
                                                            return (
                                                                <Flex key={'prod' + index} justifyContent="center" alignItems="center" className="">
                                                                    <div style={{ marginRight: 5 }}>{pi.name}</div>
                                                                    <div style={{ width: 10, height: 10, border: 'solid 1px black' }}></div>
                                                                </Flex>
                                                            )
                                                        })}
                                                    </Flex>
                                                </Td>
                                                <Td>
                                                    <Flex justifyContent="center">
                                                        <div style={{ width: 20, height: 20, border: 'solid 1px black' }}></div>
                                                    </Flex>
                                                </Td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>

                            <Flex justifyContent="flex-end" style={{ marginTop: 30 }}>
                                <div>
                                    <table style={{ fontSize: 14, border: 'solid 1px black' }}>
                                        <thead style={{ backgroundColor: '#e1e1e1', borderBottom: 'solid 1px black', textAlign: 'center' }}>
                                            <th style={{ width: 100, borderRight: 'solid 1px black' }}>封入者</th>
                                            <th style={{ width: 100 }}>二次確認者</th>
                                        </thead>
                                        <tbody>
                                            <td style={{ borderRight: 'solid 1px black' }}>
                                                <div style={{ height: 60 }}></div>
                                            </td>
                                            <td>
                                                <div style={{ height: 60 }}></div>
                                            </td>
                                        </tbody>
                                    </table>
                                </div>
                            </Flex>
                            <Flex justifyContent="space-between" style={{ marginTop: 30, position: 'absolute', bottom: 20, width: '100%', left: 0 }}>
                                <div>日付: {moment().format('YYYY/MM/DD')}</div>
                            </Flex>
                        </div>
                    </A4>
                )
            })}
        </>
    )
}

export default withRouter(PreviewRequestListPage)
